import { useEffect, useState } from 'react';
import { Plus, Edit, Power, Trash2, XCircle, Star, Users, User } from 'lucide-react';
import { supabase } from '../services/supabase';
import { loadActiveSedes, type Sede } from '../services/sedes';
import { listaEnCastellano } from '../lib/leerVarias';
import { mensajeDelGuard } from '../lib/erroresDelGuard';
import { ROLES_ASIGNABLES, type EntrenadorAsignable } from '../lib/entrenadoresAsignables';
import { OpcionesDeEntrenador } from '../components/OpcionesDeEntrenador';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import type { Database } from '../types/database.types';
import type { GroupLevel, EnrollmentStatus } from '../types/database';

type GroupRow = Database['public']['Tables']['groups']['Row'];
type SpecialClassRow = Database['public']['Tables']['special_classes']['Row'];
type SessionRow = Database['public']['Tables']['sessions']['Row'];

// `groups.class_type` no está en el manual; CHECK IN ('grupal','individual')
// pero la UI usa también 'especial' como pseudo-tipo del formulario.
type ClassType = 'grupal' | 'individual';

// Embeds que la página pide:
//   groups.select('*, profiles!monitor_id(full_name), sessions(enrollments(status)),
//                  group_categories(category)')
// El `Group` post-procesado renombra `profiles` a `monitor` y agrega
// `current_enrolled` desde el embed `sessions.enrollments`.
type GroupRowWithRelations = GroupRow & {
  profiles: { full_name: string } | null;
  sessions: { enrollments: { status: EnrollmentStatus | string }[] | null }[] | null;
  // [343] Las categorías que ACEPTA el grupo. Ojo a la diferencia con
  // `groups.category` (singular), que se queda y NO es lo mismo: esa la usa
  // `activate_subscription` para resolver el grupo de una suscripción. Esta
  // lista es la que decide si un alumno puede inscribirse.
  group_categories: { category: string }[] | null;
};

type SpecialClassRowWithRelations = SpecialClassRow & {
  profiles: { full_name: string } | null;
  // [342] La SESIÓN ESPEJO, que es sobre la que hay que llamar al motor: la
  // inscripción a una clase especial vive en `enrollments` contra esta sesión
  // (mig 232), no en `special_class_enrollments`.
  //
  // Con el nombre de la FK por delante y no `sessions(...)` a secas: un embed
  // sin desambiguar es lo que dejó /pedidos en blanco con un PGRST201 (#496).
  sessions: { id: string; status: string }[] | null;
};

interface Group {
  id: string;
  name: string;
  description: string | null;
  monitor_id: string | null;
  days_of_week: number[];
  time_start: string;
  time_end: string;
  max_students: number;
  level: GroupLevel;
  class_type: ClassType;
  category: string | null;
  // [343] «Todas» son las 7, NO la lista vacía: sin ninguna no entra nadie.
  categorias: string[];
  sede_id: string | null;
  active: boolean;
  monitor?: { full_name: string };
  current_enrolled?: number;
}

interface SpecialClass {
  id: string;
  name: string;
  description: string | null;
  date: string | null;
  time_start: string;
  time_end: string;
  max_students: number;
  status: string;
  monitor_id: string | null;
  level: string;
  sede_id: string | null;
  monitor?: { full_name: string };
  /** [342] La sesión espejo. `null` si no la tiene: hoy existe una así en
   *  producción (`DELANTEROS`), y sin ella no hay a quién cancelar. */
  sesionEspejo?: { id: string; status: string } | null;
}

const LEVEL_LABELS: Record<string, string> = {
  basico: 'Básico',
  medio: 'Medio',
  avanzado: 'Avanzado',
  tecnificacion: 'Tecnificación',
};
const LEVEL_COLORS: Record<string, string> = {
  basico: 'badge-silver',
  medio: 'badge-gold',
  avanzado: 'badge-warning',
  tecnificacion: 'badge-success',
};
const CLASS_TYPE_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
};
const CLASS_TYPE_COLORS: Record<string, string> = {
  grupal: 'badge-gold',
  individual: 'badge-silver',
  especial: 'badge-warning',
};
// Categoría por edad (mig 117; `querubin` añadida en la 192). La usa la
// suscripción mensual para resolver el grupo (PR-J4). Distinta de
// products.category (ropa).
//
// ⚠️ Esta lista está duplicada en otros tres sitios y el CHECK de la BD es
// el quinto. `supabase/__tests__/categorias-grupo.test.ts` falla si alguno
// diverge — si vienes a añadir una categoría, ese test te dice dónde más.
// Orden = orden del desplegable: de la más joven a la mayor.
const CATEGORY_LABELS: Record<string, string> = {
  querubin: 'Querubín',
  prebenjamin: 'Prebenjamín',
  benjamin: 'Benjamín',
  alevin: 'Alevín',
  infantil: 'Infantil',
  cadete: 'Cadete',
  juvenil: 'Juvenil',
};
// [343] El ORDEN sale del mapa de arriba, que es el que
// `supabase/__tests__/categorias-grupo.test.ts` compara contra el CHECK de la
// base. Escribir aquí otra lista sería el sexto sitio donde se repiten las
// siete categorías, y el test no lo vigilaría.
const CATEGORIAS = Object.keys(CATEGORY_LABELS);

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const SC_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Activa',
  completed: 'Completada',
  cancelled: 'Cancelada',
};
const SC_STATUS_COLORS: Record<string, string> = {
  scheduled: 'badge-gold',
  completed: 'badge-success',
  cancelled: 'badge-error',
};

interface GroupForm {
  sessionType: 'grupal' | 'individual' | 'especial';
  name: string;
  description: string;
  monitor_id: string;
  days_of_week: number[];
  // Solo la usa la clase especial: va en un día concreto, no en un día de la
  // semana. Sin ella el alta NO PUEDE funcionar: el trigger
  // create_session_for_special_class inserta en sessions, cuyo `date` es NOT
  // NULL. El panel la omitía y el insert fallaba en silencio.
  date: string;
  time_start: string;
  time_end: string;
  max_students: number;
  level: string;
  class_type: string;
  category: string;
  categorias: string[];
  sede_id: string;
}

const FORM_EMPTY: GroupForm = {
  sessionType: 'grupal',
  name: '',
  description: '',
  monitor_id: '',
  days_of_week: [],
  date: '',
  time_start: '17:00',
  time_end: '18:30',
  max_students: 12,
  level: 'medio',
  class_type: 'grupal',
  category: '',
  categorias: [],
  sede_id: '',
};

function GroupCard({
  g,
  sede,
  onEdit,
  onToggle,
  onDelete,
}: {
  g: Group;
  /** El nombre de su sede. Era `g.location`, un texto libre que una de las cinco
      filas de producción tenía MAL: decía Quatre Carreres con la sede en
      Castellar. El sitio es la sede. */
  sede: string | null;
  onEdit: (g: Group) => void;
  onToggle: (g: Group) => void;
  onDelete: (g: Group) => void;
}) {
  return (
    <div
      className={`card border ${g.active ? 'border-bg-border' : 'border-bg-border/50 opacity-60'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-oswald text-white uppercase tracking-wide">{g.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={LEVEL_COLORS[g.level] ?? 'badge-silver'}>
              {LEVEL_LABELS[g.level] ?? g.level}
            </span>
            <span className={CLASS_TYPE_COLORS[g.class_type ?? 'grupal'] ?? 'badge-silver'}>
              {CLASS_TYPE_LABELS[g.class_type ?? 'grupal']}
            </span>
            {g.category && (
              <span className="badge-silver" data-testid={`group-category-${g.id}`}>
                {CATEGORY_LABELS[g.category] ?? g.category}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(g)}
            className="text-text-secondary hover:text-gold-primary transition-colors"
            title="Editar"
          >
            <Edit size={14} />
          </button>
          <button
            onClick={() => onToggle(g)}
            className="text-text-secondary hover:text-error transition-colors"
          >
            <Power size={14} />
          </button>
          {!g.active && (
            <button
              onClick={() => onDelete(g)}
              className="text-text-secondary hover:text-error transition-colors"
              title="Eliminar definitivamente"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-text-secondary">{g.monitor?.full_name ?? 'Sin entrenador'}</p>
        <div className="flex gap-1">
          {DAYS.map((d, i) => (
            <span
              key={i}
              className={`w-7 h-7 rounded flex items-center justify-center text-xs font-inter font-medium ${g.days_of_week.includes(i) ? 'bg-gold-primary text-bg-primary' : 'bg-bg-elevated text-text-secondary'}`}
            >
              {d.slice(0, 2)}
            </span>
          ))}
        </div>
        <p className="text-white font-inter">
          {g.time_start} — {g.time_end}
        </p>
        <div className="flex items-center justify-between pt-1 border-t border-bg-border">
          <p className="text-text-secondary text-xs">{sede ?? 'Sin sede'}</p>
          <p className="font-oswald text-gold-primary">{g.max_students}</p>
        </div>
      </div>
    </div>
  );
}

// Las tres lecturas de `loadData` iban en un Promise.all destructurando solo
// `data`. `sedes` ya tenía su aviso (mismo fallo, arreglado antes); las otras
// tres seguían callando: sin grupos la pantalla dice «Sin sesiones grupales
// creadas», y sin entrenadores el desplegable de asignar sale vacío.
const QUE = {
  grupos: 'las sesiones grupales',
  monitores: 'los entrenadores',
  especiales: 'las clases especiales',
} as const;

export default function GruposPage() {
  const [grupos, setGrupos] = useState<Group[]>([]);
  const [specialClasses, setSpecialClasses] = useState<SpecialClass[]>([]);
  const [monitores, setMonitores] = useState<EntrenadorAsignable[]>([]);
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [sedesError, setSedesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Group | null>(null);
  const [editTargetSC, setEditTargetSC] = useState<SpecialClass | null>(null);
  const [form, setForm] = useState<GroupForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  // PR-T2.1 #3 — la categoría es obligatoria para grupos (la usa la activación de
  // suscripción para resolver el grupo). Validación de UI, sin NOT NULL en BD
  // (hay grupos legacy con category NULL que no queremos romper).
  const [formError, setFormError] = useState('');
  // Las acciones de fila (activar, borrar, cancelar) no pasan por el modal:
  // necesitan su propio sitio donde contar que algo ha fallado.
  const [pageError, setPageError] = useState('');
  const [listasFallidas, setListasFallidas] = useState<string[]>([]);
  const [confirmToggle, setConfirmToggle] = useState<Group | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Group | null>(null);
  const [confirmCancelSC, setConfirmCancelSC] = useState<SpecialClass | null>(null);
  const [confirmDeleteSC, setConfirmDeleteSC] = useState<SpecialClass | null>(null);
  // [342] Cuántos apuntados pierde la clase si se cancela. `null` es «todavía no
  // lo sé», y se dice así en el texto: cancelar a ciegas y cancelar sabiendo que
  // afecta a nueve familias no son la misma decisión.
  const [afectadosSC, setAfectadosSC] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [groupsRes, monitoresRes, especialesRes, sedeList] = await Promise.all([
      supabase
        .from('groups')
        .select(
          '*, profiles!monitor_id(full_name), sessions(enrollments(status)), group_categories(category)'
        )
        .order('name'),
      supabase
        .from('profiles')
        // V-4: también los directores. «Alevin A» la lleva uno, y sin esto no
        // había a quién elegir. `role` viene porque el desplegable los agrupa
        // aparte: mezclarlos sin distinguir sería peor que no ofrecerlos.
        .select('id, full_name, role')
        .in('role', ROLES_ASIGNABLES)
        .eq('active', true)
        .order('full_name'),
      supabase
        .from('special_classes')
        .select(
          '*, profiles!monitor_id(full_name), sessions!sessions_special_class_id_fkey(id, status)'
        )
        .order('name'),
      loadActiveSedes(),
    ]);
    // ⚠️ De `sedes` sale `defaultSedeId` unas líneas más abajo: con una sola
    // sede la asigna sola. Un fallo de lectura dejaba la lista vacía, así que
    // el grupo nuevo se creaba SIN sede en un club que sí las tiene, y sin que
    // nadie lo dijera. Por eso el aviso, y no un desplegable vacío.
    setSedesError(sedeList.error);
    setSedes(sedeList.sedes);
    const { data: gs } = groupsRes;
    const { data: ms } = monitoresRes;
    const { data: scs } = especialesRes;
    setListasFallidas([
      ...(groupsRes.error ? [QUE.grupos] : []),
      ...(monitoresRes.error ? [QUE.monitores] : []),
      ...(especialesRes.error ? [QUE.especiales] : []),
    ]);
    const groupRows = (gs ?? []) as unknown as GroupRowWithRelations[];
    setGrupos(
      groupRows.map<Group>((g) => ({
        ...g,
        level: g.level as GroupLevel,
        class_type: g.class_type as ClassType,
        monitor: g.profiles ?? undefined,
        categorias: (g.group_categories ?? []).map((c) => c.category),
        current_enrolled: (g.sessions ?? [])
          .flatMap((s) => s.enrollments ?? [])
          .filter((e) => e.status === 'confirmed').length,
      }))
    );
    setMonitores((ms ?? []) as EntrenadorAsignable[]);
    const scRows = (scs ?? []) as unknown as SpecialClassRowWithRelations[];
    setSpecialClasses(
      scRows.map((sc) => ({
        ...sc,
        monitor: sc.profiles ?? undefined,
        // [342] El embed llega como lista (es un 1-a-N en el esquema, aunque el
        // trigger cree exactamente una). Se coge la primera y se deja `null`
        // explícito cuando no hay: eso significa «esta clase no tiene espejo»,
        // no «aún no lo he leído».
        sesionEspejo: sc.sessions?.[0] ?? null,
      }))
    );
    setLoading(false);
  }

  // Preselección: si solo hay una sede activa, viene elegida por defecto; con ≥2
  // el admin la elige conscientemente (queda vacía y la validación la exige).
  const defaultSedeId = sedes.length === 1 ? sedes[0].id : '';

  function openCreate() {
    setEditTarget(null);
    setEditTargetSC(null);
    setFormError('');
    setForm({
      ...FORM_EMPTY,
      sede_id: defaultSedeId,
    });
    setModal(true);
  }

  function openEdit(g: Group) {
    setEditTarget(g);
    setEditTargetSC(null);
    setFormError('');
    setForm({
      sessionType: g.class_type === 'individual' ? 'individual' : 'grupal',
      name: g.name,
      description: g.description ?? '',
      monitor_id: g.monitor_id ?? '',
      days_of_week: g.days_of_week,
      // Grupal e individual no llevan fecha: se repiten por día de la semana.
      date: '',
      time_start: g.time_start,
      time_end: g.time_end,
      max_students: g.max_students,
      level: g.level,
      class_type: g.class_type ?? 'grupal',
      category: g.category ?? '',
      categorias: g.categorias,
      sede_id: g.sede_id ?? defaultSedeId,
    });
    setModal(true);
  }

  function openEditSC(sc: SpecialClass) {
    setFormError('');
    setEditTarget(null);
    setEditTargetSC(sc);
    setForm({
      sessionType: 'especial',
      name: sc.name,
      description: sc.description ?? '',
      monitor_id: sc.monitor_id ?? '',
      days_of_week: [],
      date: sc.date ?? '',
      time_start: sc.time_start,
      time_end: sc.time_end,
      max_students: sc.max_students,
      level: sc.level,
      class_type: 'especial',
      category: '',
      categorias: [],
      sede_id: sc.sede_id ?? defaultSedeId,
    });
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditTargetSC(null);
    setFormError('');
  }

  function toggleDay(d: number) {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));
  }

  async function handleSave() {
    setFormError('');
    // Grupal (no especial, no individual): categoría obligatoria. Individual NO
    // lleva categoría (mig 151): la suscripción individual resuelve por class_type.
    // [343] Sin ninguna categoría marcada NO entra nadie —falla cerrado, y es
    // deliberado en la base—, así que el formulario no deja guardar un grupo
    // así: el fallo cerrado tiene que ser una decisión, no un descuido.
    if (form.sessionType !== 'especial' && form.categorias.length === 0) {
      setFormError('Marca quién puede apuntarse: sin ninguna categoría no podrá entrar nadie.');
      return;
    }
    if (form.sessionType === 'grupal' && !form.category) {
      setFormError('Selecciona una categoría (edad): la necesita la activación de suscripciones.');
      return;
    }
    // Sede OBLIGATORIA para los 3 tipos (grupal/individual/especial). PF (GYM) no
    // pasa por aquí, así que no se ve afectada. La franja individual hereda la
    // sede del grupo (mig 151) → basta exigirla aquí.
    if (!form.sede_id) {
      setFormError('Selecciona una sede.');
      return;
    }
    // Fecha OBLIGATORIA en la clase especial. No es cosmética: sin ella el
    // trigger create_session_for_special_class intenta crear la sesión con
    // date NULL y el alta entera se revierte (23502).
    if (form.sessionType === 'especial' && !form.date) {
      setFormError('Selecciona la fecha de la clase especial.');
      return;
    }
    setSaving(true);
    if (form.sessionType === 'especial') {
      const scPayload = {
        name: form.name,
        description: form.description || null,
        date: form.date,
        time_start: form.time_start,
        time_end: form.time_end,
        max_students: form.max_students,
        monitor_id: form.monitor_id || null,
        level: form.level,
        sede_id: form.sede_id,
        status: 'scheduled',
      };
      const { error } = editTargetSC
        ? await supabase.from('special_classes').update(scPayload).eq('id', editTargetSC.id)
        : await supabase.from('special_classes').insert(scPayload);
      if (error) {
        setFormError(`No se pudo guardar la clase especial: ${error.message}`);
        setSaving(false);
        return;
      }
    } else {
      const payload = {
        name: form.name,
        description: form.description || null,
        monitor_id: form.monitor_id || null,
        days_of_week: form.days_of_week,
        time_start: form.time_start,
        time_end: form.time_end,
        max_students: form.max_students,
        level: form.level,
        class_type: form.sessionType,
        category: form.category || null,
        sede_id: form.sede_id,
      };
      const res = editTarget
        ? await supabase
            .from('groups')
            .update(payload)
            .eq('id', editTarget.id)
            .select('id')
            .single()
        : await supabase.from('groups').insert(payload).select('id').single();
      if (res.error) {
        setFormError(`No se pudo guardar la sesión: ${res.error.message}`);
        setSaving(false);
        return;
      }
      // [343] Las categorías que acepta, en su tabla.
      //
      // ⚠️ PRIMERO SE AÑADEN Y DESPUÉS SE QUITAN, y el orden importa: si
      // fallara el segundo paso el grupo se queda aceptando ALGUNA DE MÁS, que
      // es recuperable. Al revés se quedaría sin ninguna, y sin ninguna no
      // puede entrar nadie: un fallo a mitad habría cerrado el grupo en
      // silencio.
      const grupoId = res.data?.id ?? editTarget?.id;
      if (grupoId) {
        const ins = await supabase
          .from('group_categories')
          .upsert(
            form.categorias.map((c) => ({ group_id: grupoId, category: c })),
            { onConflict: 'group_id,category', ignoreDuplicates: true }
          );
        if (ins.error) {
          setFormError(`No se pudieron guardar las categorías: ${ins.error.message}`);
          setSaving(false);
          return;
        }
        const sobran = (editTarget?.categorias ?? []).filter((c) => !form.categorias.includes(c));
        if (sobran.length) {
          const del = await supabase
            .from('group_categories')
            .delete()
            .eq('group_id', grupoId)
            .in('category', sobran);
          if (del.error) {
            setFormError(
              `El grupo se guardó, pero sigue aceptando ${sobran
                .map((c) => CATEGORY_LABELS[c] ?? c)
                .join(', ')}: ${del.error.message}`
            );
            setSaving(false);
            return;
          }
        }
      }
    }
    await loadData();
    setModal(false);
    setEditTargetSC(null);
    setSaving(false);
  }

  async function toggleActivo(g: Group) {
    setPageError('');
    const { error } = await supabase.from('groups').update({ active: !g.active }).eq('id', g.id);
    if (error) {
      setPageError(`No se pudo cambiar el estado de "${g.name}": ${error.message}`);
      return;
    }
    setGrupos((prev) => prev.map((x) => (x.id === g.id ? { ...x, active: !x.active } : x)));
  }

  async function deleteGroup(g: Group) {
    setPageError('');
    const { data: sessions, error: sesError } = await supabase
      .from('sessions')
      .select('id')
      .eq('group_id', g.id);
    if (sesError) {
      setPageError(`No se pudieron leer las sesiones de "${g.name}": ${sesError.message}`);
      return;
    }
    const ids = ((sessions ?? []) as Pick<SessionRow, 'id'>[]).map((s) => s.id);
    // El borrado va en cascada a mano: si un paso falla hay que PARAR, o se
    // queda a medias sin que nadie se entere.
    if (ids.length) {
      const { error: enrError } = await supabase.from('enrollments').delete().in('session_id', ids);
      if (enrError) {
        // [350] El guard de la base salta AQUÍ, en el primer paso. Su texto
        // SUSTITUYE al mensaje entero: dentro del prefijo, la persona leería
        // primero «no se pudieron borrar las inscripciones», que es un paso que
        // no ha pedido —pulsó «Eliminar definitivamente»—.
        setPageError(
          mensajeDelGuard(enrError) ??
            `No se pudieron borrar las inscripciones: ${enrError.message}`
        );
        return;
      }
      const { error: sesDelError } = await supabase.from('sessions').delete().in('id', ids);
      if (sesDelError) {
        setPageError(
          mensajeDelGuard(sesDelError) ?? `No se pudieron borrar las sesiones: ${sesDelError.message}`
        );
        return;
      }
    }
    const { error } = await supabase.from('groups').delete().eq('id', g.id);
    if (error) {
      setPageError(mensajeDelGuard(error) ?? `No se pudo borrar "${g.name}": ${error.message}`);
      return;
    }
    await loadData();
  }

  // [342] Abre el diálogo Y pregunta a cuántos afecta. El recuento sale de
  // `admin_cancel_session_preview`, que es la gemela de
  // `admin_reactivate_session_preview`: la cuenta la hace el servidor, que es
  // quien ve las inscripciones de todos.
  async function abrirCancelarSC(sc: SpecialClass) {
    setAfectadosSC(null);
    setConfirmCancelSC(sc);
    if (!sc.sesionEspejo) return;
    const { data, error } = await supabase.rpc('admin_cancel_session_preview', {
      p_session_id: sc.sesionEspejo.id,
    });
    const r = data as { ok?: boolean; afectados?: number } | null;
    // Si la lectura falla, el recuento se queda en `null` y el texto lo dice.
    // Un 0 inventado haría pensar que no hay nadie dentro.
    if (!error && r?.ok && typeof r.afectados === 'number') setAfectadosSC(r.afectados);
  }

  // [342] CANCELAR PASA POR EL MOTOR, y nada más.
  //
  // Antes esto hacía dos `UPDATE` a pelo —uno a `special_classes` y otro a
  // `sessions`— y con eso dejaba la clase y su sesión en `cancelled` SIN
  // devolver ni un bono, sin cancelar ni una inscripción y sin avisar a nadie.
  // El chaval se quedaba sin clase y sin sus 20 €.
  //
  // Ahora se llama a `admin_cancel_session` sobre la SESIÓN ESPEJO, que es
  // donde viven las inscripciones (mig 232), y el motor hace lo que ya sabía
  // hacer: cancelar, devolver el bono —con validez nueva del pack, mig 342—,
  // avisar por persona y vaciar la lista de espera. La clase la sigue el
  // trigger `sessions_cancela_su_clase_especial`, así que aquí ya no se toca.
  async function cancelSpecialClass(sc: SpecialClass) {
    setPageError('');
    if (!sc.sesionEspejo) {
      // No es teórico: `DELANTEROS` está así en producción hoy.
      setPageError(
        `"${sc.name}" no tiene sesión asociada, así que no hay nada que cancelar. Avisa a soporte antes de borrarla.`
      );
      return;
    }
    const { data, error } = await supabase.rpc('admin_cancel_session', {
      p_session_id: sc.sesionEspejo.id,
    });
    const r = data as { ok?: boolean; error?: string; refunded?: number } | null;
    if (error || !r?.ok) {
      setPageError(`No se pudo cancelar "${sc.name}": ${r?.error ?? error?.message ?? 'error'}`);
      return;
    }
    await loadData();
  }

  // [342] BORRAR, DETRÁS DE CANCELAR. El orden es el que impone la FK
  // (`sessions.special_class_id` es NO ACTION): primero la sesión, luego la
  // clase. Y el candado de verdad está en la base, porque `special_classes`
  // tiene policy `ALL … is_admin()` y un DELETE directo es un camino real:
  // borrar la sesión sin cancelar arrastraba las inscripciones en CASCADE con
  // el bono gastado dentro.
  async function deleteSpecialClass(sc: SpecialClass) {
    setPageError('');
    if (sc.status !== 'cancelled') {
      setPageError(`Cancela "${sc.name}" antes de borrarla: así se devuelven los bonos.`);
      return;
    }
    const { error: sesError } = await supabase
      .from('sessions')
      .delete()
      .eq('special_class_id', sc.id);
    if (sesError) {
      setPageError(`No se pudieron borrar las sesiones de "${sc.name}": ${sesError.message}`);
      return;
    }
    const { error } = await supabase.from('special_classes').delete().eq('id', sc.id);
    if (error) {
      setPageError(`No se pudo borrar "${sc.name}": ${error.message}`);
      return;
    }
    await loadData();
  }

  const isEspecial = form.sessionType === 'especial';

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={openCreate} className="btn-gold flex items-center gap-2">
          <Plus size={16} /> Nueva Sesión
        </button>
      </div>

      {pageError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="grupos-page-error"
        >
          {pageError}
        </p>
      )}

      {listasFallidas.length > 0 && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="grupos-listas-fallidas"
          role="status"
        >
          No se han podido cargar {listaEnCastellano([...listasFallidas])}. Lo que salga vacío no
          significa que no exista: no crees nada encima sin comprobarlo.
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Sesiones Grupales */}
          <div>
            <h2 className="font-oswald text-white uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
              <Users size={14} className="text-gold-primary" /> Sesiones Grupales
            </h2>
            {grupos.filter((g) => g.class_type !== 'individual').length === 0 ? (
              <p
                className={
                  listasFallidas.includes(QUE.grupos)
                    ? 'text-error text-sm'
                    : 'text-text-secondary text-sm'
                }
                data-testid="grupos-vacio"
              >
                {listasFallidas.includes(QUE.grupos)
                  ? 'No se han podido cargar.'
                  : 'Sin sesiones grupales creadas.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {grupos
                  .filter((g) => g.class_type !== 'individual')
                  .map((g) => (
                    <GroupCard
                      key={g.id}
                      g={g}
                      sede={sedes.find((x) => x.id === g.sede_id)?.name ?? null}
                      onEdit={openEdit}
                      onToggle={setConfirmToggle}
                      onDelete={setConfirmDelete}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* Sesiones Individuales */}
          <div>
            <h2 className="font-oswald text-white uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
              <User size={14} className="text-silver" /> Sesiones Individuales
            </h2>
            {grupos.filter((g) => g.class_type === 'individual').length === 0 ? (
              <p className="text-text-secondary text-sm">
                Sin sesiones individuales. Crea una de tipo "Individual" (aforo 1) y genera sus
                sesiones desde la Agenda, igual que un grupo.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {grupos
                  .filter((g) => g.class_type === 'individual')
                  .map((g) => (
                    <GroupCard
                      key={g.id}
                      g={g}
                      sede={sedes.find((x) => x.id === g.sede_id)?.name ?? null}
                      onEdit={openEdit}
                      onToggle={setConfirmToggle}
                      onDelete={setConfirmDelete}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* Clases Especiales */}
          <div>
            <h2 className="font-oswald text-white uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
              <Star size={14} className="text-warning" /> Clases Especiales
            </h2>
            {specialClasses.length === 0 ? (
              <p className="text-text-secondary text-sm">
                Sin clases especiales. Crea una plantilla y prográmala en diferentes fechas desde la
                Agenda.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {specialClasses.map((sc) => (
                  <div
                    key={sc.id}
                    className={`card border border-warning/30 ${sc.status === 'cancelled' ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-oswald text-white uppercase tracking-wide">
                          {sc.name}
                        </h3>
                        <span className={SC_STATUS_COLORS[sc.status] ?? 'badge-gold'}>
                          {SC_STATUS_LABELS[sc.status] ?? sc.status}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditSC(sc)}
                          className="text-text-secondary hover:text-gold-primary transition-colors"
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                        {sc.status === 'scheduled' && (
                          <button
                            onClick={() => abrirCancelarSC(sc)}
                            className="text-text-secondary hover:text-error transition-colors"
                            title="Desactivar"
                          >
                            <XCircle size={14} />
                          </button>
                        )}
                        {sc.status === 'cancelled' && (
                          <button
                            onClick={() => setConfirmDeleteSC(sc)}
                            className="text-text-secondary hover:text-error transition-colors"
                            title="Eliminar definitivamente"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={LEVEL_COLORS[sc.level] ?? 'badge-silver'}>
                          {LEVEL_LABELS[sc.level] ?? sc.level}
                        </span>
                      </div>
                      <p className="text-text-secondary">
                        {sc.monitor?.full_name ?? 'Sin entrenador'}
                      </p>
                      <p className="text-white font-inter">
                        {sc.date ? `${sc.date} · ` : ''}
                        {sc.time_start.slice(0, 5)} — {sc.time_end.slice(0, 5)}
                      </p>
                      <p className="text-text-secondary text-xs">
                        {sedes.find((x) => x.id === sc.sede_id)?.name ?? 'Sin sede'}
                      </p>
                      <div className="flex items-center justify-between pt-1 border-t border-bg-border">
                        <p className="text-text-secondary text-xs">{sc.max_students} plazas</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal
        open={modal}
        onClose={closeModal}
        title={
          editTarget ? 'Editar Sesión' : editTargetSC ? 'Editar Clase Especial' : 'Nueva Sesión'
        }
        size="lg"
      >
        <div className="grid grid-cols-2 gap-4">
          {/* Selector de tipo: solo al crear */}
          {!editTarget && !editTargetSC && (
            <div className="col-span-2">
              <label className="label">Tipo de sesión *</label>
              <div className="flex gap-2">
                {/* Individual = MISMO motor que grupal salvo aforo=1 (mig 151). Al
                    elegirlo se fija max_students=1 y NO se pide categoría; el resto
                    del flujo (generate_sessions + suscripción J4 + waitlist) es igual. */}
                {(['grupal', 'individual', 'especial'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        sessionType: t,
                        class_type: t,
                        ...(t === 'individual' ? { max_students: 1, category: '' } : {}),
                      }))
                    }
                    className={
                      form.sessionType === t
                        ? 'btn-gold text-sm flex-1'
                        : 'btn-ghost text-sm flex-1'
                    }
                  >
                    {CLASS_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="col-span-2">
            <label className="label">Nombre *</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="col-span-2">
            <label className="label">Descripción</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          {/* Sede: obligatoria para los 3 tipos (grupal/individual/especial).
              Preseleccionada si solo hay una activa. PF (GYM) no pasa por aquí. */}
          <div className="col-span-2">
            <label className="label">Sede *</label>
            <select
              className="input"
              data-testid="group-form-sede"
              value={form.sede_id}
              onChange={(e) => setForm((f) => ({ ...f, sede_id: e.target.value }))}
            >
              <option value="">Selecciona…</option>
              {sedes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {sedesError && (
              <p className="text-error text-xs mt-1" data-testid="grupo-sedes-error">
                No se han podido cargar las sedes, así que esta lista está incompleta. Guarda el
                grupo cuando vuelva a verse.
              </p>
            )}
          </div>

          {/* Campos comunes para todos los tipos */}
          <div>
            <label className="label">Entrenador</label>
            <select
              className="input"
              value={form.monitor_id}
              onChange={(e) => setForm((f) => ({ ...f, monitor_id: e.target.value }))}
            >
              <option value="">Sin asignar</option>
              <OpcionesDeEntrenador lista={monitores} />
            </select>
          </div>
          <div>
            <label className="label">Nivel</label>
            <select
              className="input"
              value={form.level}
              onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
            >
              {Object.entries(LEVEL_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          {/* Categoría (edad): solo grupal. Individual (mig 151) no la usa. */}
          {form.sessionType === 'grupal' && (
            <div>
              <label className="label">Categoría (edad) *</label>
              <select
                className="input"
                data-testid="group-form-category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="">Selecciona…</option>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-secondary mt-1">
                Obligatoria: la usa la activación de suscripciones para asignar el grupo.
              </p>
            </div>
          )}
          {/* [343] QUIÉN PUEDE APUNTARSE. Es otra cosa que el desplegable de
              arriba, y por eso están separados: ese resuelve el grupo de una
              suscripción, y esto decide si un alumno cabe. Un grupo puede
              aceptar varias categorías o todas —la tecnificación lleva TODAS,
              porque una especialidad no tiene edad—. */}
          {form.sessionType !== 'especial' && (
            <div>
              <label className="label">Quién puede apuntarse *</label>
              <div className="flex flex-wrap gap-2 mt-1">
                <button
                  type="button"
                  data-testid="group-form-cats-todas"
                  className={`px-3 py-1.5 rounded text-xs font-inter font-medium transition-all ${
                    form.categorias.length === CATEGORIAS.length
                      ? 'bg-gold-primary text-bg-primary'
                      : 'bg-bg-elevated text-text-secondary border border-bg-border hover:border-silver'
                  }`}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      categorias: f.categorias.length === CATEGORIAS.length ? [] : [...CATEGORIAS],
                    }))
                  }
                >
                  Todas
                </button>
                {CATEGORIAS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`group-form-cat-${c}`}
                    className={`px-3 py-1.5 rounded text-xs font-inter font-medium transition-all ${
                      form.categorias.includes(c)
                        ? 'bg-gold-primary text-bg-primary'
                        : 'bg-bg-elevated text-text-secondary border border-bg-border hover:border-silver'
                    }`}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        categorias: f.categorias.includes(c)
                          ? f.categorias.filter((x) => x !== c)
                          : [...f.categorias, c],
                      }))
                    }
                  >
                    {CATEGORY_LABELS[c]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-text-secondary mt-1">
                {form.categorias.length === 0
                  ? '⚠️ Sin ninguna marcada no podrá apuntarse nadie.'
                  : form.categorias.length === CATEGORIAS.length
                    ? 'Todas: cualquier alumno puede apuntarse.'
                    : `Solo podrán apuntarse: ${form.categorias
                        .map((c) => CATEGORY_LABELS[c] ?? c)
                        .join(', ')}.`}
              </p>
            </div>
          )}
          <div>
            <label className="label">Máximo alumnos</label>
            <input
              type="number"
              min={1}
              max={100}
              className="input"
              // Individual (mig 151): aforo FIJO = 1 (el motor común produce
              // "1 confirmado, resto lista de espera").
              disabled={form.sessionType === 'individual'}
              value={form.sessionType === 'individual' ? 1 : form.max_students}
              onChange={(e) => setForm((f) => ({ ...f, max_students: +e.target.value }))}
            />
            {form.sessionType === 'individual' && (
              <p className="text-xs text-text-secondary mt-1">
                Fijo a 1 en tecnificación individual.
              </p>
            )}
          </div>
          {/* Fecha: SOLO la clase especial. Grupal e individual se repiten por
              días de la semana y no la usan. */}
          {isEspecial && (
            <div className="col-span-2">
              <label className="label">Fecha *</label>
              <input
                type="date"
                className="input"
                data-testid="group-form-date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
              <p className="text-xs text-text-secondary mt-1">
                Obligatoria: la clase especial crea su sesión en ese día concreto.
              </p>
            </div>
          )}
          <div>
            <label className="label">Hora inicio</label>
            <input
              type="time"
              className="input"
              value={form.time_start}
              onChange={(e) => setForm((f) => ({ ...f, time_start: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Hora fin</label>
            <input
              type="time"
              className="input"
              value={form.time_end}
              onChange={(e) => setForm((f) => ({ ...f, time_end: e.target.value }))}
            />
          </div>

          {/* Días de la semana: solo grupal e individual */}
          {!isEspecial && (
            <div className="col-span-2">
              <label className="label">Días de la semana</label>
              <div className="flex gap-2 mt-1">
                {DAYS.map((d, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`w-10 h-10 rounded text-sm font-inter font-medium transition-all ${form.days_of_week.includes(i) ? 'bg-gold-primary text-bg-primary' : 'bg-bg-elevated text-text-secondary border border-bg-border hover:border-silver'}`}
                  >
                    {d.slice(0, 2)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {formError && (
          <p className="text-error text-sm mt-3" data-testid="group-form-error">
            {formError}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-4">
          <button onClick={closeModal} className="btn-ghost">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-gold">
            {saving
              ? 'Guardando...'
              : editTarget || editTargetSC
                ? 'Guardar cambios'
                : 'Crear sesión'}
          </button>
        </div>
      </Modal>

      <Confirm
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        onConfirm={() => confirmToggle && toggleActivo(confirmToggle)}
        title={confirmToggle?.active ? 'Desactivar sesión' : 'Activar sesión'}
        message={`¿Seguro que quieres ${confirmToggle?.active ? 'desactivar' : 'activar'} "${confirmToggle?.name}"?`}
        danger={confirmToggle?.active}
      />
      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteGroup(confirmDelete)}
        title="Eliminar sesión definitivamente"
        message={`¿Eliminar "${confirmDelete?.name}" y todas sus sesiones e inscripciones históricas? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        danger
      />
      <Confirm
        open={!!confirmCancelSC}
        onClose={() => setConfirmCancelSC(null)}
        onConfirm={() => confirmCancelSC && cancelSpecialClass(confirmCancelSC)}
        title="Cancelar clase especial"
        message={
          // [342] El texto dice a quién afecta y qué va a pasar con su dinero.
          // El de antes decía «las sesiones se marcarán como canceladas» y no
          // era verdad del todo: no decía que no se devolvía nada, porque
          // entonces no se devolvía.
          `¿Cancelar "${confirmCancelSC?.name}"? ` +
          (afectadosSC === null
            ? 'No se ha podido leer a cuántos apuntados afecta. '
            : afectadosSC === 0
              ? 'No hay nadie apuntado. '
              : `Afecta a ${afectadosSC} ${afectadosSC === 1 ? 'apuntado' : 'apuntados'}. `) +
          'A cada uno se le devolverá su bono con validez nueva y se le avisará.'
        }
        confirmLabel="Cancelar la clase"
        danger
      />
      <Confirm
        open={!!confirmDeleteSC}
        onClose={() => setConfirmDeleteSC(null)}
        onConfirm={() => confirmDeleteSC && deleteSpecialClass(confirmDeleteSC)}
        title="Eliminar clase especial definitivamente"
        message={
          // [342] Borrar va detrás de cancelar, igual que en una sesión normal.
          confirmDeleteSC && confirmDeleteSC.status !== 'cancelled'
            ? `"${confirmDeleteSC.name}" no está cancelada. Cancélala primero: así se devuelven los bonos antes de que el borrado se lleve las inscripciones.`
            : `¿Eliminar "${confirmDeleteSC?.name}"? Esta acción no se puede deshacer.`
        }
        confirmLabel="Eliminar"
        danger
      />
    </div>
  );
}
